import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Briefcase, Save, Check, X } from "lucide-react";

const fmtMoney = (n) => `S$${formatNum(n || 0, { decimals: 0 })}`;
const CAT_LABEL = { packages: "Packages", implementation: "Implementation", advisory: "Advisory", value: "Business Value", managed: "Managed" };
const STATUS_OPTS = ["purchased", "active", "scoping", "in_delivery", "delivered", "cancelled"];

export default function ServicesAdmin() {
  const loc = useLocation();
  const [tab, setTab] = useState(loc.pathname.includes("engagement") ? "engagements" : "catalogue");
  const [services, setServices] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [prices, setPrices] = useState({});
  const [quotes, setQuotes] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/services/platform/catalog").then((r) => setServices(r.data.services || [])).catch((e) => toast.error(apiErr(e)));
    api.get("/services/platform/engagements").then((r) => setEngagements(r.data.engagements || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    setBusy(s.code);
    try { await api.patch(`/services/platform/catalog/${s.code}`, { enabled: !s.enabled }); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const savePrice = async (s) => {
    const v = parseFloat(prices[s.code]);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid price"); return; }
    setBusy(s.code);
    try { await api.patch(`/services/platform/catalog/${s.code}`, { price_sgd: v }); toast.success("Price updated."); setPrices((p) => ({ ...p, [s.code]: "" })); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const setQuote = async (e) => {
    const v = parseFloat(quotes[e.id]);
    if (isNaN(v) || v <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(e.id);
    try { await api.post(`/services/platform/engagements/${e.id}/quote`, { amount_sgd: v }); toast.success("Quote sent to tenant."); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(""); }
  };
  const setStatus = async (e, status) => {
    setBusy(e.id);
    try { await api.post(`/services/platform/engagements/${e.id}/status`, { status }); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(""); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="services-admin-page">
      <div>
        <div className="label-eyebrow mb-1">Platform · Professional Services</div>
        <h1 className="font-display text-3xl font-bold tracking-tighter flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-accent" /> Professional Services
        </h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="catalogue" data-testid="admin-tab-catalogue">Service Catalogue</TabsTrigger>
          <TabsTrigger value="engagements" data-testid="admin-tab-engagements">Engagements & Quotes</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogue" className="mt-5">
          <div className="card-flat divide-y divide-border/50" data-testid="admin-catalogue">
            {services.map((s) => (
              <div key={s.code} className="flex items-center gap-3 p-3" data-testid={`admin-service-${s.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.name} {s.overridden_price && <span className="text-[9px] text-accent">(repriced)</span>}</div>
                  <div className="text-[11px] text-muted-foreground">{CAT_LABEL[s.category]} · {s.price_display}</div>
                </div>
                {s.buy_type !== "quote" && (
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs text-muted-foreground w-16 text-right">{fmtMoney(s.price_sgd)}</span>
                    <Input value={prices[s.code] || ""} onChange={(ev) => setPrices((p) => ({ ...p, [s.code]: ev.target.value }))}
                      placeholder="new" className="h-8 w-24 text-xs" data-testid={`price-input-${s.code}`} />
                    <Button size="sm" variant="ghost" disabled={busy === s.code} onClick={() => savePrice(s)} data-testid={`save-price-${s.code}`}><Save className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
                <Button size="sm" variant={s.enabled ? "outline" : "primary"} disabled={busy === s.code} onClick={() => toggle(s)} data-testid={`toggle-${s.code}`}>
                  {s.enabled ? <><X className="h-3.5 w-3.5" /> Disable</> : <><Check className="h-3.5 w-3.5" /> Enable</>}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="engagements" className="mt-5">
          <div className="card-flat divide-y divide-border/50" data-testid="admin-engagements">
            {!engagements.length && <div className="py-12 text-center text-muted-foreground text-sm">No engagements yet.</div>}
            {engagements.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`admin-engagement-${e.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-sm">{e.service_name}</div>
                  <div className="text-[11px] text-muted-foreground">{e.tenant_name} · {e.sow_ref} · {e.price_sgd ? fmtMoney(e.price_sgd) : "—"}</div>
                </div>
                <Badge variant="outline" className="capitalize">{(e.status || "").replace(/_/g, " ")}</Badge>
                {e.status === "quote_requested" && (
                  <div className="flex items-center gap-1">
                    <Input value={quotes[e.id] || ""} onChange={(ev) => setQuotes((q) => ({ ...q, [e.id]: ev.target.value }))}
                      placeholder="price S$" className="h-8 w-24 text-xs" data-testid={`quote-input-${e.id}`} />
                    <Button size="sm" variant="primary" disabled={busy === e.id} onClick={() => setQuote(e)} data-testid={`send-quote-${e.id}`}>Send quote</Button>
                  </div>
                )}
                {["purchased", "active", "scoping", "in_delivery", "delivered"].includes(e.status) && (
                  <select value="" onChange={(ev) => ev.target.value && setStatus(e, ev.target.value)} data-testid={`status-select-${e.id}`}
                    className="h-8 text-xs rounded-md border border-border bg-transparent px-2">
                    <option value="">Set status…</option>
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
